import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCompanyDatabases, useMutationSetActiveDatabase, useMutationRemoveDatabase, useQueryCompanyDatabaseSizes, isPendingDatabaseStatus } from '@/lib/api/company/useCompanyDatabaseHooks'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { CompanyDatabaseModel } from '@/lib/api/company/companyDatabases'
import { useTable } from '@/modules/drivers/context/TableContext'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableCheckboxHeader from '@/modules/drivers/shared/tableItems/TableCheckboxHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { TableSortIcon } from '@/modules/drivers/shared/tableItems/TableSortIcon'
import AddUserColor from '@/assets/icons_new/AddUserColor.svg'
import CloseGray from '@/assets/icons_new/CloseGray.svg'
import MoveDatabaseModal from './MoveDatabaseModal'
import UpdateLabelModal from './UpdateLabelModal'

interface CompanyDatabasesPanelProps {
  companyId: string
  onDuplicate: () => void
  onAddDatabase: () => void
}

import type { SortDirection } from '@/modules/drivers/types/table'

type SortKey = 'databaseName' | 'label' | 'status' | 'currentVersion'

const getColumns = (t: (key: string) => string): { title: string; key: SortKey; sort: boolean }[] => [
  { title: t('administrationPanel-companies.database-name'), key: 'databaseName', sort: true },
  { title: t('administrationPanel-companies.database-label'), key: 'label', sort: true },
  { title: t('administrationPanel-companies.database-status'), key: 'status', sort: true },
  { title: t('administrationPanel-companies.database-version'), key: 'currentVersion', sort: true },
]

function formatSize(bytes: number | null | undefined): string {
  if (bytes == null) return '-'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function compareValues(a: string | null | undefined, b: string | null | undefined, direction: SortDirection): number {
  const valA = (a ?? '').toLowerCase()
  const valB = (b ?? '').toLowerCase()
  if (valA < valB) return direction === 'asc' ? -1 : 1
  if (valA > valB) return direction === 'asc' ? 1 : -1
  return 0
}

const CompanyDatabasesPanel: React.FC<CompanyDatabasesPanelProps> = ({
  companyId,
  onDuplicate,
  onAddDatabase,
}) => {
  const { t } = useLocalize()
  const COLUMNS = React.useMemo(() => getColumns(t), [t])
  const { data, isLoading } = useQueryCompanyDatabases(companyId)
  const databases = React.useMemo(() => data ?? [], [data])
  const { data: sizeData, isLoading: isSizesLoading } = useQueryCompanyDatabaseSizes(companyId)
  const setActiveMutation = useMutationSetActiveDatabase(companyId)
  const removeMutation = useMutationRemoveDatabase(companyId)

  const sizeMap = React.useMemo(() => {
    if (!sizeData) return new Map<number, number | null>()
    return new Map(sizeData.map((s) => [s.databaseId, s.sizeBytes]))
  }, [sizeData])

  const totalSizeBytes = React.useMemo(() => {
    if (!sizeData) return null
    const values = sizeData.map((s) => s.sizeBytes).filter((v): v is number => v != null)
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) : null
  }, [sizeData])
  const [moveTarget, setMoveTarget] = React.useState<{ databaseId: number; databaseName: string } | null>(null)
  const [labelTarget, setLabelTarget] = React.useState<{ databaseId: number; currentLabel: string | null } | null>(null)

  const [sortConfig, setSortConfig] = React.useState<{ key: SortKey; direction: SortDirection } | null>(null)

  const {
    toggleAll,
    toggleId,
    selectedGroup,
    setAllIds,
    isSelectedAny,
    isActiveGroup,
    resetAll,
  } = useTable()

  React.useEffect(() => {
    setAllIds(
      databases.map((db) => ({
        id: db.id,
        isActive: db.isActive,
      })),
    )
  }, [databases])

  const handleSetActive = async (databaseId: number) => {
    if (!confirm(t('administrationPanel-companies.database-set-active-confirm'))) {
      return
    }
    await setActiveMutation.mutateAsync({ companyId, databaseId })
  }

  const handleRemove = async (databaseId: number) => {
    if (!confirm(t('administrationPanel-companies.database-remove-confirm'))) {
      return
    }
    await removeMutation.mutateAsync({ companyId, databaseId })
  }

  const handleRemoveSelected = async () => {
    if (!selectedGroup) return
    const selectedDbs = selectedGroup.ids
      .map((id) => databases.find((d) => d.id === id))
      .filter((db): db is CompanyDatabaseModel => !!db)
    if (selectedDbs.some((db) => db.isStarter)) {
      alert(t('databases.cannot-detach-starter'))
      return
    }
    const count = selectedDbs.length
    if (!confirm(t('databases.remove-selected-confirm').replace('{count}', String(count)))) {
      return
    }
    for (const db of selectedDbs) {
      await removeMutation.mutateAsync({ companyId, databaseId: db.databaseId })
    }
    resetAll()
  }

  const handleHeaderClick = (key: string) => {
    if (isSelectedAny) return
    setSortConfig((prev) => {
      if (prev?.key === key) {
        if (prev.direction === 'asc') return { key: key as SortKey, direction: 'desc' }
        return null
      }
      return { key: key as SortKey, direction: 'asc' }
    })
  }

  const sortedDatabases = React.useMemo(() => {
    if (!sortConfig) return databases
    return [...databases].sort((a, b) => {
      const key = sortConfig.key
      const valA = a[key] as string | null | undefined
      const valB = b[key] as string | null | undefined
      return compareValues(valA, valB, sortConfig.direction)
    })
  }, [databases, sortConfig])

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col">
      {/* Action bar */}
      <section className="tw-relative tw-bg-content-primary">
        {isSelectedAny ? (
          <div className="tw-relative tw-z-20 tw-flex tw-w-full tw-justify-between tw-border-b-2 tw-border-primary tw-bg-[#F1F4FF] tw-px-4 tw-py-3.5 lg:tw-items-center">
            <div className="tw-absolute -tw-bottom-1.5 tw-left-3">
              <div className="tw-absolute -tw-top-1.5 !tw-z-10 tw-h-2 tw-w-2 tw-bg-[#F1F4FF]"></div>
              <div className="tw-h-2 tw-w-2 tw-rotate-45 tw-bg-primary"></div>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-5 lg:tw-flex-row lg:tw-items-center">
              <p className="tw-space-x-2 tw-text-sm">
                <span>{t('common.selected')}</span>
                <span className="tw-font-medium">{selectedGroup?.ids.length}</span>/{databases.length} :
              </p>
              <div
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-primary tw-px-3 tw-py-1.5 hover:tw-opacity-90"
                onClick={onDuplicate}
              >
                <p className="tw-text-sm tw-font-medium tw-text-white">
                  {t('administrationPanel-companies.duplicate-database')}
                </p>
              </div>
              {isActiveGroup === false && selectedGroup?.ids.length === 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const db = databases.find((d) => d.id === selectedGroup.ids[0])
                    if (db) handleSetActive(db.databaseId)
                  }}
                  disabled={setActiveMutation.isPending}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-blue-50 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-blue-700 hover:tw-bg-blue-100 disabled:tw-opacity-50"
                >
                  {t('databases.set-active')}
                </button>
              )}
              {isActiveGroup === false && selectedGroup?.ids.length === 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const db = databases.find((d) => d.id === selectedGroup.ids[0])
                    if (db?.isStarter) {
                      alert(t('databases.cannot-move-starter'))
                      return
                    }
                    if (db) setMoveTarget({ databaseId: db.databaseId, databaseName: db.databaseName })
                  }}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-amber-50 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-amber-700 hover:tw-bg-amber-100"
                >
                  {t('databases.move')}
                </button>
              )}
              {selectedGroup?.ids.length === 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const db = databases.find((d) => d.id === selectedGroup.ids[0])
                    if (db) setLabelTarget({ databaseId: db.databaseId, currentLabel: db.label })
                  }}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-bg-purple-50 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-purple-700 hover:tw-bg-purple-100"
                >
                  {t('databases.change-label')}
                </button>
              )}
              <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>
              <button
                type="button"
                onClick={handleRemoveSelected}
                disabled={
                  removeMutation.isPending ||
                  selectedGroup?.ids.some((id) => databases.find((d) => d.id === id)?.isStarter)
                }
                title={
                  selectedGroup?.ids.some((id) => databases.find((d) => d.id === id)?.isStarter)
                    ? t('databases.cannot-detach-starter')
                    : undefined
                }
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 tw-text-sm tw-font-medium tw-text-red-600 hover:tw-bg-red-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              >
                {t('databases.detach-selected')}
              </button>
            </div>
            <div className="tw-relative tw-cursor-pointer" onClick={() => resetAll()}>
              <div className="tw-absolute -tw-top-1.5 tw-left-1/2 tw-z-0 tw-h-7 tw-w-7 tw--translate-x-1/2 tw-rounded-full tw-bg-white tw-p-2 tw-shadow lg:tw-top-1/2 lg:tw--translate-y-1/2"></div>
              <img className="tw-z-1 tw-relative" src={CloseGray} alt="Close Icon" />
            </div>
          </div>
        ) : (
          <div className="tw-relative tw-z-[100] tw-w-full">
            <div className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-py-1.5 md:tw-items-center">
              <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-center">
                <p className="tw-text-sm">
                  {t('databases.databases-count-label')}: <span className="tw-font-medium">{databases.length}</span>
                </p>
                {totalSizeBytes != null && (
                  <>
                    <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>
                    <p className="tw-text-sm">
                      {t('databases.total-size')}: <span className="tw-font-medium">{formatSize(totalSizeBytes)}</span>
                    </p>
                  </>
                )}
                {isSizesLoading && (
                  <>
                    <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>
                    <p className="tw-text-sm tw-text-gray-400">{t('databases.loading-sizes')}</p>
                  </>
                )}
                <div className="tw-hidden tw-h-5 tw-w-0.5 tw-bg-secondary lg:tw-block"></div>
                <div
                  onClick={onAddDatabase}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-p-2 hover:tw-bg-base-300 hover:tw-opacity-90"
                >
                  <img src={AddUserColor} alt="Add Database Icon" />
                  <p className="tw-text-sm tw-font-medium tw-text-primary">
                    {t('databases.add-database')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Table */}
      <div className="tw-z-10 tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        {isLoading ? (
          <div className="tw-grid tw-place-items-center tw-py-16">
            <LoaderIcon className="tw-w-16 tw-animate-spin" />
          </div>
        ) : sortedDatabases.length > 0 ? (
          <div className="tw-h-full tw-overflow-y-auto">
            <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
              <thead className="notches-border--right tw-w-full tw-select-none tw-border-b tw-bg-content-primary tw-z-50 tw-sticky tw-top-0">
                <tr>
                  <TableCheckboxHeader
                    disabled={false}
                    toggleAll={toggleAll}
                    isSelectedAny={isSelectedAny}
                  />
                  {COLUMNS.map((col) => (
                    <SortableHeader
                      key={col.key}
                      title={col.title}
                      sort={col.sort}
                      sortDirection={sortConfig?.key === col.key && col.sort ? sortConfig.direction : null}
                      onClick={() => col.sort && handleHeaderClick(col.key)}
                      isSelectedAny={isSelectedAny}
                    />
                  ))}
                  <th className="tw-relative tw-min-w-fit tw-max-w-[200px] tw-text-nowrap tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-content-base tw-text-xs tw-opacity-80">
                    <p className="tw-font-base tw-uppercase">{t('databases.size')}</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedDatabases.map((db: CompanyDatabaseModel) => (
                  <DatabaseRow
                    key={db.id}
                    db={db}
                    sizeBytes={sizeMap.get(db.databaseId)}
                    isSizesLoading={isSizesLoading}
                    isChecked={selectedGroup?.ids.includes(db.id) ?? false}
                    isActiveGroup={isActiveGroup}
                    isSelectedAny={isSelectedAny}
                    toggleId={toggleId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
              <thead className="notches-border--right tw-w-full tw-select-none tw-border-b tw-bg-content-primary">
                <tr>
                  <TableCheckboxHeader
                    disabled={true}
                    toggleAll={toggleAll}
                    isSelectedAny={isSelectedAny}
                  />
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="tw-relative tw-min-w-fit tw-max-w-[200px] tw-text-nowrap tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-content-base tw-text-xs tw-opacity-80"
                    >
                      <p className="tw-font-base tw-uppercase">{col.title}</p>
                    </th>
                  ))}
                  <th className="tw-relative tw-min-w-fit tw-max-w-[200px] tw-text-nowrap tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-content-base tw-text-xs tw-opacity-80">
                    <p className="tw-font-base tw-uppercase">{t('databases.size')}</p>
                  </th>
                </tr>
              </thead>
            </table>
            <TableEmpty title={t('databases.no-databases')} />
          </>
        )}
      </div>

      <MoveDatabaseModal
        isOpen={moveTarget != null}
        close={() => setMoveTarget(null)}
        companyId={companyId}
        databaseId={moveTarget?.databaseId ?? 0}
        databaseName={moveTarget?.databaseName ?? ''}
      />
      <UpdateLabelModal
        isOpen={labelTarget != null}
        close={() => setLabelTarget(null)}
        companyId={companyId}
        databaseId={labelTarget?.databaseId ?? 0}
        currentLabel={labelTarget?.currentLabel ?? ''}
      />
    </div>
  )
}

interface DatabaseRowProps {
  db: CompanyDatabaseModel
  sizeBytes: number | null | undefined
  isSizesLoading: boolean
  isChecked: boolean
  isActiveGroup: boolean | undefined
  isSelectedAny: boolean
  toggleId: (id: number, isActive: boolean) => void
}

const DatabaseRow: React.FC<DatabaseRowProps> = ({
  db,
  sizeBytes,
  isSizesLoading,
  isChecked,
  isActiveGroup,
  isSelectedAny,
  toggleId,
}) => {
  const { t } = useLocalize()
  const [isHovered, setIsHovered] = React.useState(false)
  const isActive = db.isActive
  const isDisabled = isSelectedAny && isActiveGroup !== isActive

  return (
    <tr
      className={`notches-border--right tw-w-fit tw-cursor-pointer tw-border-b tw-bg-white hover:tw-bg-primary/10 ${isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''} ${!isActive && isChecked ? 'tw-opacity-50' : !isActive ? 'tw-opacity-30' : ''}`}
      onClick={() => !isDisabled && toggleId(db.id, isActive)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCheckbox
        disabled={isDisabled}
        checked={isChecked}
        onChange={() => toggleId(db.id, isActive)}
        isHovered={isHovered}
      />
      <TableTextRow>
        <div className="tw-flex tw-items-center tw-gap-2">
          {db.databaseName}
          {db.isActive && (
            <span className="tw-rounded tw-bg-green-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-800">
              {t('common.active')}
            </span>
          )}
          {db.isStarter && (
            <span className="tw-rounded tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-700">
              {t('databases.starter-badge')}
            </span>
          )}
        </div>
      </TableTextRow>
      <TableTextRow>{db.label || '-'}</TableTextRow>
      <TableTextRow>
        {isPendingDatabaseStatus(db.status) ? (
          <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-400">
            <LoaderIcon className="tw-w-3.5 tw-animate-spin tw-shrink-0" />
            {t('databases.status-creating')}
          </span>
        ) : (
          db.status || '-'
        )}
      </TableTextRow>
      <TableTextRow>{db.currentVersion || '-'}</TableTextRow>
      <TableTextRow>
        {isSizesLoading ? (
          <span className="tw-text-gray-400">…</span>
        ) : (
          formatSize(sizeBytes)
        )}
      </TableTextRow>
    </tr>
  )
}

const SortableHeader: React.FC<{
  title: string
  sort: boolean
  sortDirection: SortDirection
  onClick: () => void
  isSelectedAny: boolean
}> = ({ title, sort, sortDirection, onClick, isSelectedAny }) => {
  const [hover, setHover] = React.useState(false)

  return (
    <th
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={isSelectedAny ? undefined : onClick}
      className="tw-relative tw-min-w-fit tw-max-w-[200px] tw-cursor-pointer tw-text-nowrap tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-content-base tw-text-xs tw-opacity-80"
    >
      <div className="tw-flex tw-items-center tw-gap-1">
        <p className="tw-font-base tw-uppercase">{title}</p>
        {sort && <TableSortIcon hover={hover} sortDirection={sortDirection} />}
      </div>
    </th>
  )
}

export default CompanyDatabasesPanel
